<template>
    <Accordion :activeIndex="activeAccordionTabIndex" :multiple="false">
        <AccordionTab
            v-for="(clinic, index) in props.sortedClinics"
            :key="clinic.id"
            :disabled="true"
            :pt-options="{
                mergeProps: true,
                mergeSections: true,
            }"
            :pt="{
                header: ({ props: ptProps }) => ({
                    class: [{ '!select-auto !pointer-events-auto !cursor-auto !opacity-100': ptProps == null ? void 0 : ptProps.disabled }],
                }),
                headerAction: () => ({
                    class: ['!p-0 border-none', isSelected(clinic) ? 'bg-select-item-bg-color' : 'bg-item-bg-color hover:!bg-item-bg-hover-color'],
                }),
                content: { class: '!p-0 border-none' },
                headericon: { class: 'text-clinic-title-c ml-2 absolute right-4 !mr-0' },
            }"
        >
            <template #header>
                <div @click="onClinicOpen(index)" :id="`clinic-${clinic.id}`" class="w-full font-medium">
                    <div class="flex items-center p-4 xl:flex-row xl:px-8 xl:pl-4">
                        <img v-if="clinic.logoSquare" class="my-0 mr-4 size-11 rounded-full" :class="{ 'opacity-50': isInactive(clinic) }" :src="loadLogo(clinic) || ''" alt="clinic-icon" />
                        <div v-else class="my-0 mr-4">
                            <FontAwesomeLayers class="fa-3x w-full text-clinic-icon-active-c">
                                <FontAwesomeIcon icon="fa-sharp fa-thin fa-circle" />
                                <FontAwesomeIcon icon="fa-sharp fa-solid fa-location-dot" transform="shrink-7" />
                            </FontAwesomeLayers>
                        </div>
                        <div class="mr-6 flex h-fit w-full flex-col items-start justify-between md:flex-row md:items-center xl:mr-2" :class="{ 'text-white': isInactive(clinic) }">
                            <h2 class="mb-2 text-clinic-title-c">{{ clinic.name }}</h2>
                            <span class="text-xs text-clinic-street-subt-c" v-html="getAddress(clinic)"></span>
                        </div>
                    </div>
                </div>
            </template>

            <div class="grid w-full grid-cols-2 gap-x-2 gap-y-8 rounded-b-xl rounded-t-none bg-select-item-bg-color p-4 xl:mt-0 xl:grid-cols-2 xl:p-8">
                <div class="block">
                    <OpenHours v-if="clinic.workHourList?.length > 0" :dateList="clinic.workHourList" />
                    <div class="grid grid-cols-1 gap-2">
                        <a v-if="clinic.phone" class="flex w-fit flex-row items-center" :href="`tel:${clinic.phone}`">
                            <FontAwesomeIcon icon="fa-sharp fa-solid fa-phone" fixed-width class="text-clinic-description-c" />
                            <span class="ml-2 text-xs text-clinic-contact-c">{{ formatPhoneNumber(null, clinic.phone) }}</span>
                        </a>
                        <a v-if="clinic.email" class="flex w-fit flex-row items-center" :href="`mailto:${clinic.email}`">
                            <FontAwesomeIcon icon="fa-sharp fa-solid fa-envelope" fixed-width class="text-clinic-description-c" />
                            <span class="ml-2 text-xs text-clinic-contact-c">{{ clinic.email }}</span>
                        </a>
                        <a v-if="clinic.twitter" class="flex w-fit flex-row items-center" :href="fullUrl(clinic.twitter)" target="_blank">
                            <FontAwesomeIcon icon="fa-brands fa-square-x-twitter" fixed-width class="text-clinic-description-c" />
                            <span class="ml-2 text-xs text-clinic-contact-c">Twitter</span>
                        </a>
                        <a v-if="clinic.facebook" class="flex w-fit flex-row items-center" :href="fullUrl(clinic.facebook)" target="_blank">
                            <FontAwesomeIcon icon="fa-brands fa-square-facebook" fixed-width class="text-clinic-description-c" />
                            <span class="ml-2 text-xs text-clinic-contact-c">Facebook</span>
                        </a>
                        <a v-if="clinic.instagram" class="flex w-fit flex-row items-center" :href="fullUrl(clinic.instagram)" target="_blank">
                            <FontAwesomeIcon icon="fa-brands fa-square-instagram" fixed-width class="text-clinic-description-c" />
                            <span class="ml-2 text-xs text-clinic-contact-c">Instagram</span>
                        </a>
                        <a v-if="clinic.web" class="flex w-fit flex-row items-center" :href="fullUrl(clinic.web)" target="_blank">
                            <FontAwesomeIcon icon="fa-sharp fa-solid fa-globe" fixed-width class="text-clinic-description-c" />
                            <span class="ml-2 text-xs text-clinic-contact-c">{{ getURL(clinic) }}</span>
                        </a>
                        <p class="mb-4 text-xs font-light text-clinic-description-c">
                            {{ clinic.description }}
                        </p>
                    </div>
                </div>
                <div v-show="!mapError && !isMapLoading" class="ml-auto mr-6 xl:mr-2">
                    <div class="relative size-32 cursor-pointer max-[375px]:size-24 sm:size-36 md:size-64" @click="onMapPointClick">
                        <div class="absolute inset-0 z-10 flex items-center justify-center">
                            <FontAwesomeIcon @click="onMapPointClick" size="2x" icon="fa-sharp fa-solid fa-location-dot" class="text-blue-400 -translate-y-1/2" />
                        </div>
                        <img class="size-full rounded-xl object-cover grayscale" :src="mapURL" alt="map" />
                    </div>
                </div>
            </div>
        </AccordionTab>
    </Accordion>
    <ConfirmDialog
        :draggable="false"
        :pt-options="{
            mergeProps: true,
            mergeSections: true,
        }"
        :pt="{
            footer: () => ({
                class: ['gap-6'],
            }),
        }"
        group="templating"
    >
        <template #message="slotProps">
            <div class="flex-column border-bottom-1 flex w-full items-center gap-6">
                <FontAwesomeIcon icon="fa-sharp fa-solid fa-exclamation-triangle" class="text-4xl text-danger" />
                <p>{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>
</template>

<script setup lang="ts">
import { FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { computed, nextTick, onMounted, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import OpenHours from '@/components/Clinics/OpenHours.vue';
import { IExtentedClinic } from '@/interfaces/IExtendedClinic';
import { useWizardStore } from '@/stores/wizard';
import { fileUrl } from '@/utils/fileUrl';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { fullUrl } from '@/utils/fullUrl';

const props = defineProps({
    sortedClinics: {
        type: Array<IExtentedClinic>,
        default: () => [],
    },
});

const wizardStore = useWizardStore();
const { t } = useI18n();
const confirm = useConfirm();

const mapError = ref(false);
const mapSrc = ref('');
const isMapLoading = ref(true);

const activeAccordionTabIndex = ref(null);
const pendingTabIndex = ref(null);

onMounted(async () => {
    await loadMap();

    const index = props.sortedClinics.findIndex((clinic) => clinic.id === wizardStore.getSelectedClinic?.id);
    activeAccordionTabIndex.value = index === -1 ? null : index;

    await scrollToClinic();
});

const isInactive = (clinic: IExtentedClinic) => {
    return computed(() => clinic?.services?.length === 0).value;
};

const isSelected = (clinic: IExtentedClinic) => {
    return computed(() => wizardStore.getSelectedClinic?.id === clinic.id).value;
};

const getAddress = (clinic: IExtentedClinic) => {
    if (!clinic || !clinic.city || !clinic.street || !clinic.post) {
        return '';
    }
    return `${clinic.street}, ${clinic.post}, <span class="font-bold">${clinic.city}</span>`;
};

const loadLogo = (clinic: IExtentedClinic) => {
    try {
        if (clinic.logoSquare) {
            if (clinic?.logoSquare !== '') {
                return fileUrl(clinic.logoSquare, 100, 100, 0);
            }
        }
    } catch (error) {
        console.error(error);
        return '';
    }
};

const getURL = (clinic: IExtentedClinic) => {
    return new URL(fullUrl(clinic.web)).host;
};

const mapURL = computed(() => {
    if (!wizardStore.getSelectedClinic?.geoCache?.idMap) {
        return '';
    } else {
        return fileUrl(wizardStore.getSelectedClinic.geoCache.idMap, 100, 100, 0);
    }
});

watch(
    () => wizardStore.getSelectedClinic?.id,
    async () => {
        await scrollToClinic();
        await loadMap();
    }
);

const loadMap = (): Promise<void> => {
    return new Promise((resolve) => {
        if (mapURL.value) {
            const img = new Image();
            img.src = mapURL.value;
            isMapLoading.value = true;

            img.onload = async () => {
                mapSrc.value = img.src;
                mapError.value = false;
                isMapLoading.value = false;
                resolve();
            };

            img.onerror = async () => {
                mapSrc.value = '';
                mapError.value = true;
                isMapLoading.value = false;
                resolve();
            };
        } else {
            mapError.value = false;
            isMapLoading.value = false;
            resolve();
        }
    });
};

const onMapPointClick = () => {
    if (!wizardStore.getSelectedClinic) {
        return;
    }
    const { street, post, city } = toRefs(wizardStore.getSelectedClinic);
    if (!street?.value || !post?.value || !city?.value) {
        return;
    }
    window.open(`https://maps.google.com/?q=${street.value},${post.value},${city.value}`);
};

const scrollToClinic = async () => {
    await nextTick(() => {
        setTimeout(() => {
            const clinicElement = document.getElementById(`clinic-${wizardStore.getSelectedClinic?.id}`);
            if (clinicElement) {
                clinicElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300);
    });
};

const onClinicOpen = async (index: number) => {
    pendingTabIndex.value = null;

    const clinic = props.sortedClinics[index];

    if (!clinic || isInactive(clinic) || wizardStore.getSelectedClinic?.id === clinic.id || !clinic.id) {
        return;
    }

    pendingTabIndex.value = index;

    // Reset selected staff and service if the clinic is changed
    if (wizardStore.getSelectedStaff || wizardStore.getSelectedService) {
        confirm.require({
            group: 'templating',
            message: t('confirmChangeClinic'),
            header: t('confirmChangeClinicHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptClass: 'bg-[#00aeef] text-white rounded-2xl hover:opacity-50 !text-sm !p-2.5',
            rejectClass: 'bg-danger text-white rounded-2xl hover:opacity-50 text-sm !p-2.5',
            rejectLabel: t('cancelButton'),
            acceptLabel: t('confirmButton'),
            accept: async () => {
                wizardStore.resetService();
                wizardStore.resetStaff();
                wizardStore.selectClinic({ ...clinic });
                activeAccordionTabIndex.value = pendingTabIndex.value;
            },
            reject: () => {
                pendingTabIndex.value = null;
            },
        });
    } else {
        wizardStore.selectClinic({ ...clinic });
        activeAccordionTabIndex.value = pendingTabIndex.value;
    }
};
</script>
